import React, {useEffect, useState} from "react";
import {
  Autocomplete,
  Dialog,
  Grid,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {Box} from "@mui/system";
import TranslationUtil from "@/utils/TranslationUtil";
import {CancelButton, SavingButton} from "@/components/data-grid/CustomButton";
import {useTranslation} from "react-i18next";
import {CountryName} from "@/utils/CountryName";
import DialogTitle from "@mui/material/DialogTitle";
import NGInputLabel from "@/components/ngteco-form/NGInputLabel";
import {Controller, useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {object, TypeOf, z} from "zod";
import {DropzoneArea} from "material-ui-dropzone";
import {makeStyles} from "@material-ui/core/styles";
import {useSnackbar} from "notistack";
import UserService from "@/services/auth/UserService";
import {ReactComponent as IconUpload} from "@/assets/icons/IconUpload.svg";
import CompanyService from "@/services/auth/CompanyService";

const useStyles = makeStyles({
  dropzone: {
    width: '80%',
    minWidth: "20vw",
    height: '150px',
    minHeight: '150px',
    backgroundColor: '#fafafa',
    border: '2px dashed #eeeeee',
    borderRadius: '5px'
  },
  text: {
    fontSize: '14px !important',
    marginBottom: "0px",
    whiteSpace: 'pre-line', // 关键：允许识别 \n 换行
    textAlign: 'center',    // 关键：保持文本居中
  }
});

type LanguageMenuItemProps = {
  hidePopup?: () => Promise<void>;
  type: number;
};

const SupportMenuItem: React.FC<LanguageMenuItemProps> = (props) => {
  const {t} = useTranslation();
  const [open, setOpen] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [getData, setGetData] = useState<boolean>(false);
  const defaultPattern = {
    companyName: "",
    // impact: "medium",
    frequency: "seldom",
    contactPerson: "",
    country: "United States",
    emailAddress: "",
    contactNumber: "",
    summary: "",
    description: "",
    deviceModel: "",
    // steps2reproduce: "",
    // attachment: "",
  };
  const [country, setCountry] = useState<string>("");
  const country_name = CountryName;
  const {enqueueSnackbar} = useSnackbar();
  const patternSchema = object({
    companyName: z.string().min(1, t("common.This field is required!"))
      .max(255, t("common.The length of field cannot exceed digits", {number: 255})),
    // impact: z.string(),
    deviceModel: z.string(),
    frequency: z.string(),
    contactPerson: z.string().min(1, t("common.This field is required!"))
      .max(255, t("common.The length of field cannot exceed digits", {number: 255})),
    country: z.string().min(1, t("common.This field is required!")),
    emailAddress: z.string().min(1, t("common.This field is required!")).max(250,
      t("common.The length of field cannot exceed digits", {number: 250})),
    contactNumber: z.string().min(1, t("common.This field is required!")).max(50,
      t("common.The length of field cannot exceed digits", {number: 50})),
    summary: z.string().min(1, t("common.This field is required!")).max(255,
      t("common.The length of field cannot exceed digits", {number: 255})),
    description: z.string().min(1, t("common.This field is required!")).max(5000,
      t("common.The length of field cannot exceed digits", {number: 5000})),
    // steps2reproduce: z.string().min(1, t("common.This field is required!")).max(5000,
    //   t("common.The length of field cannot exceed digits", {number: 5000})),
    attachment: z.any(),
  });
  type PatternInput = TypeOf<typeof patternSchema>;

  const patternSettingForm = useForm<PatternInput>({
    defaultValues: defaultPattern,
    resolver: zodResolver(patternSchema),
  });
  const {
    register,
    trigger,
    setError,
    getValues,
    setValue,
    formState: {errors},
  } = patternSettingForm;

  const customIcon = (): any => {
    return () => {
      return <IconUpload style={{marginTop: '20px'}}></IconUpload>;
    };
  };
  useEffect(() => {
    UserService.userInfo().then(user_res => {
      patternSettingForm.setValue('contactPerson', user_res.data?.firstName + ' ' + user_res.data?.lastName)
      patternSettingForm.setValue('emailAddress', user_res.data?.email)
      patternSettingForm.setValue('contactNumber', user_res.data?.phone || '')
      patternSettingForm.setValue('country', user_res.data?.country)
      setCountry(user_res.data?.country)
    });
    CompanyService.companyInfo().then(res => {
      patternSettingForm.setValue('companyName', res.data?.name)
    })
  }, [getData])

  const onSubmit = async (formData: any) => {
    setLoading(true)
    const formdata = new FormData();
    Object.keys(formData).map((key: any) => {
      if (key == "attachment") {
        Object.keys(formData[key]).map((att_key: any, index: number) => {
          formdata.append(
            "attachment" + index.toString(),
            formData[key][index]
          );
        });
      } else {
        formdata.append(key, formData[key]);
      }
    });
    await UserService.supportFeedback(formdata).then((response: any) => {
      if (response?.status < 400) {
        enqueueSnackbar(t("common.success"), {
          variant: "success",
          anchorOrigin: {
            vertical: "top",
            horizontal: "center",
          },
        });
        setOpen(false);
      } else {
        enqueueSnackbar(t("common.Country field error"), {
          variant: "error",
          anchorOrigin: {
            vertical: "top",
            horizontal: "center",
          },
        });
      }
    });
    setLoading(false);
  };
  const classes = useStyles();

  return (
    <Box>
      <MenuItem
        onClick={async () => {
          await props.hidePopup?.();
          setOpen(true);
          setGetData(true);
        }}
      >
        <ListItemText>{props.type == 1 ? t("common.Contact us") : t('common.Report your issues')}</ListItemText>
      </MenuItem>
      {open && <Dialog
        open={open}
        PaperProps={{
          style: {
            // width: '1200px',   // 视窗宽度的80%
            maxWidth: '1000px', // 最大宽度
            height: '90vh',   // 视窗高度的90%
          }
        }}
      >
        <DialogTitle sx={{fontSize: "1rem"}}>
          {t("common.support tips1")}
        </DialogTitle>
        <Paper sx={{width: "80%", boxShadow: "none", marginLeft: "25px", marginTop: "-10px"}}>
          <Typography variant="h6" gutterBottom sx={{fontSize: "1rem"}}>
            {t("common.support tips2")}
          </Typography>
          {/*<Typography variant="h6" gutterBottom>*/}
          {/*  {t("common.support tips3")}*/}
          {/*</Typography>*/}
        </Paper>
        <Grid
          container={true}
          rowSpacing={1.25}
          columnSpacing={1.25}
          component={"form"}
          sx={{padding: "0 24px"}}
        >
          <Grid item xs={12} sx={{p: "0 16px"}}>
            <Grid sx={{display: "flex", gap: "10px"}}>
              <Grid container={true} columnSpacing={1.25} component={"form"}>
                <Grid item={true} xs={6}>
                  <NGInputLabel label={t("signUp.Country or Region")} required={true} sx={{fontSize: '18px'}}/>
                  <Autocomplete
                    value={country_name.find((country_list) => {
                      if (!country) {
                        setCountry('United States')
                      }
                      return country_list.value === country;
                    })}
                    isOptionEqualToValue={(option, value) => {
                      return option.value === value.value;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth={true}
                        size={"small"}
                        sx={{width: "20vw", marginTop: "-5px"}}
                      />
                    )}
                    getOptionLabel={(option) => {
                      return option.value;
                    }}
                    options={country_name}
                    onChange={(event, value) => {
                      patternSettingForm.setValue(
                        "country",
                        value?.value || ''
                      );
                      setCountry(value?.value || '')
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container={true} columnSpacing={1.25} component={"form"}>
                <Grid item={true} xs={6}>
                  <NGInputLabel
                    label={t("common.Frequency")}
                    required={true}
                    sx={{fontSize: '18px'}}
                  />
                  <Controller
                    control={patternSettingForm.control}
                    name={"frequency"}
                    render={({field, fieldState}) => (
                      <Select {...field} size={"small"} fullWidth={true} sx={{width: "20vw", marginTop: "-5px"}}>
                        <MenuItem value={"always"}>{t("common.Always")}</MenuItem>
                        <MenuItem value={"usually"}>{t("common.Usually")}</MenuItem>
                        <MenuItem value={"sometimes"}>{t("common.Sometimes")}</MenuItem>
                        <MenuItem value={"occasionally"}>
                          {t("common.Occasionally")}
                        </MenuItem>
                        <MenuItem value={"seldom"}>{t("common.Seldom")}</MenuItem>
                        <MenuItem value={"once"}>{t("common.Once")}</MenuItem>
                      </Select>
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid sx={{display: "flex"}}>
              <Grid container={true} columnSpacing={1} component={"form"}>
                <Grid item={true} xs={6}>
                  <NGInputLabel label={t("common.Device Model")} required={true} sx={{fontSize: '18px'}}/>
                  <Controller
                    control={patternSettingForm.control}
                    name={"deviceModel"}
                    render={({field, fieldState}) => (
                      <TextField
                        size="small"
                        fullWidth
                        sx={{width: "20vw", marginTop: "-5px"}}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message || " "}
                        {...field}
                        InputLabelProps={{
                          shrink: !!field.value,
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Grid container={true} component={"form"}>
                <Grid item={true} xs={6} sx={{marginLeft: "5px"}}>
                  <NGInputLabel label={t("common.Company Name")} required={true} sx={{fontSize: '18px'}}/>
                  <Controller
                    control={patternSettingForm.control}
                    name={"companyName"}
                    render={({field, fieldState}) => (
                      <TextField
                        required={true}
                        size="small"
                        fullWidth
                        sx={{width: "20vw", marginTop: "-5px"}}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message || " "}
                        {...field}
                        InputLabelProps={{
                          shrink: !!field.value,
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid sx={{display: "flex", gap: "10px", alignItems: "stretch"}}>
              <Grid
                container={true}
                columnSpacing={1.25}
                component={"form"}
                // style={{marginTop: "-20px"}}
              >
                <Grid item={true} xs={6}>
                  <Grid container={true} rowSpacing={1.25}>
                    <Grid item={true} xs={12} sx={{marginTop: "-20px"}}>
                      <NGInputLabel label={t("common.Contact Number")} required={true} sx={{fontSize: '18px'}}/>
                      <Controller
                        control={patternSettingForm.control}
                        name={"contactNumber"}
                        render={({field, fieldState}) => (
                          <TextField
                            size="small"
                            sx={{width: "100%"}}
                            fullWidth
                            multiline
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || " "}
                            {...field}
                            InputLabelProps={{
                              shrink: !!field.value,
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item={true} xs={12} sx={{marginTop: "-16px"}}>
                      <NGInputLabel
                        label={t("common.Email Address")}
                        required={true}
                        sx={{fontSize: '18px'}}
                      />
                      <Controller
                        control={patternSettingForm.control}
                        name={"emailAddress"}
                        render={({field, fieldState}) => (
                          <TextField
                            required={true}
                            sx={{width: "100%"}}
                            size="small"
                            fullWidth
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || " "}
                            {...field}
                            InputLabelProps={{
                              shrink: !!field.value,
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item={true} xs={6} sx={{marginTop: "-20px"}}>
                  <NGInputLabel label={t("common.Attachment(Max 5 files, 10MB each)")} sx={{fontSize: '18px'}}/>
                  <Controller
                    control={patternSettingForm.control}
                    name={"attachment"}
                    render={({field, fieldState}) => (
                      <div>
                        <DropzoneArea
                          showPreviews={true}
                          showPreviewsInDropzone={false}
                          classes={{ // 关键配置
                            root: classes.dropzone,
                            text: classes.text,
                          }}
                          dropzoneText={`${t("common.Drop files or click to choose")}\n${t("common.attachment support type")}`}
                          useChipsForPreview
                          previewGridProps={{
                            container: {spacing: 1, direction: "row"},
                          }}
                          dropzoneClass="supportDropzone"
                          previewText={t("common.Selected files")}
                          Icon={customIcon()}
                          acceptedFiles={['image/jpeg', 'image/png', 'application/pdf', 'application/msword',
                            'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain',
                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                            'application/vnd.ms-excel', 'text/csv', 'application/zip', 'application/x-rar-compressed',
                            'application/x-7z-compressed']}
                          maxFileSize={10485760}
                          filesLimit={5}
                          onChange={(files) => {
                            // 限制文件数量
                            const limitedFiles = files.slice(0, 5);
                            setValue("attachment", limitedFiles);
                          }}
                          getFileAddedMessage={(fileName) => {
                            return t("common.File fileName successfully added.", {fileName: fileName})
                          }}
                          getFileRemovedMessage={(fileName) => {
                            return t("common.File fileName removed.", {fileName: fileName})
                          }}
                          getDropRejectMessage={(rejectedFile, acceptedFiles, maxFileSize) => {
                            if (rejectedFile && rejectedFile.size && maxFileSize && rejectedFile.size > maxFileSize) {
                              return t('common.File fileName exceeds the maximum file size limit of 10MB.', {fileName: rejectedFile?.name})
                            }
                            if (acceptedFiles && acceptedFiles.length >= 5) {
                              return t('common.File fileName was rejected. Maximum 5 files allowed.', {fileName: rejectedFile?.name})
                            }
                            return t('common.File fileName was rejected.', {fileName: rejectedFile?.name})
                          }}
                          onDropRejected={(rejectedFiles, acceptedFiles) => {
                            // 处理文件被拒绝的情况
                            if (rejectedFiles && rejectedFiles.length > 0) {
                              rejectedFiles.forEach((file) => {
                                enqueueSnackbar(t('common.File fileName was rejected.', {fileName: file.name}), {variant: 'error'});
                              });
                            }
                          }}
                        />
                      </div>
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid sx={{display: "flex", marginTop: "-10px"}}>
              <Grid container={true} columnSpacing={1.25} component={"form"}>
                <Grid item={true} xs={12}>
                  <NGInputLabel label={t("common.Summary")} required={true} sx={{fontSize: '18px'}}/>
                  <Controller
                    control={patternSettingForm.control}
                    name={"summary"}
                    render={({field, fieldState}) => (
                      <TextField
                        sx={{marginTop: "-5px"}}
                        multiline
                        required={true}
                        // minRows={5}
                        // maxRows={5}
                        // type="text"
                        fullWidth
                        size={"small"}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message || " "}
                        {...field}
                        InputLabelProps={{
                          shrink: !!field.value,
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid sx={{display: "flex", marginTop: "-10px"}}>
              <Grid container={true} columnSpacing={1.25} component={"form"}>
                <Grid item={true} xs={12}>
                  <NGInputLabel
                    label={t("common.Description")}
                    required={true}
                    sx={{fontSize: '18px', marginTop: "-5px"}}
                  />
                  <Controller
                    control={patternSettingForm.control}
                    name={"description"}
                    render={({field, fieldState}) => (
                      <TextField
                        multiline
                        sx={{marginTop: "-5px"}}
                        required={true}
                        // minRows={5}
                        // maxRows={5}
                        rows={2}
                        type="text"
                        fullWidth
                        size={"small"}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message || " "}
                        {...field}
                        InputLabelProps={{
                          shrink: !!field.value,
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          sx={{
            borderTop: "1px solid #F2F5F8",
            height: "60px",
            width: "100%",
            paddingLeft: "24px",
            boxSizing: "border-box",
          }}
        >
          <Grid style={{paddingTop: "5px", marginBottom: "5px", marginRight: "40px"}}>
            <Stack spacing={3} direction={"row"} justifyContent="end">
              <CancelButton
                onClick={() => {
                  patternSettingForm.reset();
                  setOpen(false);
                  setGetData(false);
                  setLoading(false);
                }}
              />
              <SavingButton
                loading={isLoading}
                children={TranslationUtil.t("common.submit")}
                sx={{width: "160px", textTransform: "none"}}
                onClick={async () => {
                  if (!getValues().country) {
                    setValue('country', 'United States')
                  }
                  const isValid = await trigger();
                  if (isValid) {
                    const data = getValues();
                    await onSubmit(data);
                  }
                }}
              />
            </Stack>
          </Grid>
        </Grid>
      </Dialog>}
    </Box>
  );
};
export default SupportMenuItem;
